import { useMemo } from "react";
import { imageAssetUrl } from "@/utils/url";
import {
  Badge,
  cn,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "web-design-system";
import { format } from "date-fns";
import capitalize from "lodash/capitalize";
import { Route } from "@/routes/(auth)/(headless)/reports/families/$id.portfolio";
import {
  FamilyMemberReportSummary,
  OngoingXSIP,
  PortfolioSummary,
  PortfolioValuationHolding,
} from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { PortfolioSummaryComponent } from "@crm/index";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { MemberBar } from "@crm/components/families/member-bar";
import { PlanStatusBadge, ProductTypeBadge } from "web-core";
import { ReportFooter } from "../components/report-footer";
import { ReportHeader } from "../components/report-header";
import { ReportContainer } from "../components/report-container";
import { DistributorProfileSummary } from "../components/distributor-profile-summary";
import { EmployeePortfolioSummary } from "../components/employee-portfolio-summary";
import { AssetAllocationReport } from "../components/asset-allocation-report";
import { ProductsReport } from "../components/products-report";
import { OrdersReport } from "../components/orders-report";

const holdingsColumns: Array<{
  name: string;
  className?: string;
  render: (data: PortfolioValuationHolding) => React.ReactNode;
}> = [
  {
    name: "Scheme name",
    render: (data) => (
      <div className="flex flex-row gap-2 items-center">
        <AmcLogo amc_code={data.morningstar_amc_code} />
        <span className="text-sm">{data.scheme_name}</span>
        {data.product_type ? (
          <ProductTypeBadge type={data.product_type} />
        ) : null}
      </div>
    ),
  },
  {
    name: "Source",
    render: (data) => (
      <span className="text-sm">
        <Badge
          variant="custom"
          className={cn(
            data.internal_flag
              ? "bg-tag-slate text-tag-slate-foreground"
              : "bg-tag-yellow text-tag-yellow-foreground"
          )}
        >
          {data.internal_flag ? "Internal" : "External"}
        </Badge>
      </span>
    ),
  },
  {
    name: "Invested",
    render: (data) => (
      <span className="text-sm">{convertToCurrency(data.invested, 0)}</span>
    ),
  },
  {
    name: "Current",
    render: (data) => (
      <span className="text-sm">{convertToCurrency(data.current, 0)}</span>
    ),
  },
  {
    name: "Returns",
    render: (data) => {
      const value = data.absolute_return;
      const absoluteReturn = data.absolute_return_pct;
      const isPositive = Number(absoluteReturn) > 0;
      return (
        <div
          className={cn(
            "text-sm",
            isPositive ? "text-green-700" : "text-destructive"
          )}
        >
          <span>
            {convertToCurrency(value ?? 0, 0)}&nbsp;(
            {formatAsPercentage(absoluteReturn ?? "")})
          </span>
        </div>
      );
    },
  },
  {
    name: "XIRR",
    render: (data) => {
      const lifetimeXirr = Number(data.lifetime_xirr);
      const xirr = Number(data.xirr);
      const value =
        lifetimeXirr && !Number.isNaN(lifetimeXirr) ? lifetimeXirr : xirr;
      if (Number.isNaN(value)) return null;
      const type = value > 0 ? "postive" : value === 0 ? "zero" : "negative";
      return (
        <div
          className={cn(
            "text-sm",
            type === "postive" && "text-constructive",
            type === "negative" && "text-destructive",
            type === "zero" && "text-muted-foreground"
          )}
        >
          <span>{formatAsPercentage(value ?? "")}</span>
        </div>
      );
    },
  },
  {
    name: "NAV",
    render: (data) => (
      <span className="text-sm">{convertToCurrency(data.nav, 0)}</span>
    ),
  },
];

const sipColumns: Array<{
  name: string;
  render: (data: OngoingXSIP) => React.ReactNode;
}> = [
  {
    name: "Scheme name",
    render: (data) => (
      <div className="flex flex-row gap-2 items-center">
        <AmcLogo amc_code={data.morningstar_amc_code} />
        <span className="text-sm">{data.scheme_name}</span>
      </div>
    ),
  },
  {
    name: "Status",
    render: (data) => <PlanStatusBadge status={data.status} />,
  },
  {
    name: "Next Installment",
    render: (data) => {
      if (!data.installment_due_date) return "-";
      return <span>{format(data.installment_due_date, "dd MMM yyyy")}</span>;
    },
  },
  {
    name: "Frequency",
    render: (data) => (
      <span className="text-sm">{capitalize(data.frequency)}</span>
    ),
  },
  {
    name: "Amount",
    render: (data) => (
      <span className="text-sm">{data.installment_amount}</span>
    ),
  },
];

function memberColumns(totalCurrent: number): Array<{
  name: string;
  render: (member: FamilyMemberReportSummary) => React.ReactNode;
}> {
  return [
    {
      name: "Family member",
      render: (member) => (
        <MemberBar
          name={member.member}
          display="name"
          holdingPattern={member.holding_pattern}
          percentage={totalCurrent ? (member.current / totalCurrent) * 100 : 0}
        />
      ),
    },
    {
      name: "Current",
      render: (member) => (
        <span className="text-sm">{convertToCurrency(member.current, 0)}</span>
      ),
    },
    {
      name: "Invested",
      render: (member) => (
        <span className="text-sm">{convertToCurrency(member.invested, 0)}</span>
      ),
    },
    {
      name: "Return",
      render: (member) => {
        const isPositive = Number(member.return_pct) > 0;
        return (
          <div
            className={cn(
              "text-sm",
              isPositive ? "text-green-700" : "text-destructive"
            )}
          >
            <span>
              {convertToCurrency(member.return_amount, 0)}&nbsp;(
              {formatAsPercentage(member.return_pct ?? "")})
            </span>
          </div>
        );
      },
    },
    {
      name: "XIRR",
      render: (member) => {
        const value = member.lifetime_xirr ? member.lifetime_xirr : member.xirr;
        if (value == null) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        const type = value > 0 ? "postive" : value === 0 ? "zero" : "negative";
        return (
          <div
            className={cn(
              "text-sm",
              type === "postive" && "text-constructive",
              type === "negative" && "text-destructive",
              type === "zero" && "text-muted-foreground"
            )}
          >
            <span>{formatAsPercentage(value)}</span>
          </div>
        );
      },
    },
    {
      name: "SIP book",
      render: (member) => (
        <span className="text-sm">{convertToCurrency(member.sip_book, 0)}</span>
      ),
    },
  ];
}

export function FamilyPortfolioReport() {
  const { date_range, end_date } = Route.useSearch();
  const {
    family,
    distributorProfile,
    distributorBranding,
    employee,
    summary,
    productBreakdownItems,
    allocationBuckets,
    holdings,
    memberSummary,
    sips,
    transactions,
  } = Route.useLoaderData();
  const isEmployee = !!employee;
  const isTillToday = date_range === "till_today";

  const sortedHoldings = useMemo(
    () =>
      [...holdings].sort((a, b) => a.scheme_name.localeCompare(b.scheme_name)),
    [holdings]
  );

  const sortedSips = useMemo(
    () =>
      sips
        .filter((s) => s.status === "ACTIVE")
        .sort((a, b) => a.scheme_name.localeCompare(b.scheme_name)),
    [sips]
  );

  const totalMemberCurrent = useMemo(
    () => memberSummary.reduce((total, member) => total + member.current, 0),
    [memberSummary]
  );
  const columns = useMemo(
    () => memberColumns(totalMemberCurrent),
    [totalMemberCurrent]
  );

  return (
    <>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content px-8 w-full">
          <div className="flex h-[calc(100vh-150px)] flex-row">
            <div className="flex flex-col flex-1 h-full pt-15 pb-4 justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-semibold">{family?.name}</span>
                <span className="text-sm text-muted-foreground">
                  {family?.primary_contact}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-4xl">
                  Portfolio Valuation Report
                </span>
                <span className="text-sm text-muted-foreground">
                  Generated on {format(new Date(), "dd MMM yyyy")}
                </span>
              </div>
              {isEmployee ? (
                <EmployeePortfolioSummary
                  employee={employee}
                  distributorProfile={distributorProfile}
                />
              ) : (
                <DistributorProfileSummary
                  distributorProfile={distributorProfile}
                />
              )}
            </div>
            <div className="flex-1 p-15 relative pb-0 bg-linear-to-b from-slate-200 to-slate-50 dark:from-slate-800 dark:to-slate-900 h-full rounded-lg items-center flex flex-col">
              <span className="text-[22px] font-semibold">
                Track your portfolio daily on Creso App
              </span>
              <div className="w-full h-[75%] absolute bottom-0">
                <img
                  src={imageAssetUrl("download-app.webp")}
                  alt="family-portfolio"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-row items-center justify-between mt-15 print:mt-0 mb-5">
            <h3 className="break-before-page">Mutual Fund Portfolio</h3>
            <span className="text-sm text-muted-foreground">
              {isTillToday
                ? `As of ${format(new Date(), "dd MMM yyyy")}`
                : end_date
                  ? `As of ${format(new Date(end_date), "dd MMM yyyy")}`
                  : `As of ${format(new Date(), "dd MMM yyyy")}`}
            </span>
          </div>
          <PortfolioSummaryComponent
            isLoading={false}
            summary={summary as PortfolioSummary}
          />

          {memberSummary.length ? (
            <div className="mt-8">
              <Table className="border border-border rounded-lg">
                <TableHeader className="bg-muted rounded-t-lg">
                  <TableRow>
                    {columns.map((column) => (
                      <TableHead key={column.name}>{column.name}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberSummary.map((member, index) => (
                    <TableRow key={`${member.member}-${index}`}>
                      {columns.map((column) => (
                        <TableCell key={column.name}>
                          {column.render(member)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          <div className="mt-8 print:mt-0 break-before-page">
            <ProductsReport products={productBreakdownItems} />
          </div>
          <div className="flex flex-col gap-4 mt-8">
            <h4>Asset Allocation</h4>
            <AssetAllocationReport buckets={allocationBuckets} />
          </div>

          {sortedHoldings.length ? (
            <div className="flex flex-col mt-8 print:mt-0 gap-4 break-before-page">
              <h4>Holdings</h4>
              <Table className="border border-border rounded-lg">
                <TableHeader className="bg-muted rounded-t-lg">
                  <TableRow>
                    {holdingsColumns.map((column) => (
                      <TableHead key={column.name}>{column.name}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedHoldings.map((holding, index) => (
                    <TableRow
                      key={`${holding.scheme_isin}-${holding.source}-${index}`}
                    >
                      {holdingsColumns.map((column) => (
                        <TableCell key={column.name}>
                          {column.render(holding)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          {sortedSips.length ? (
            <div className="flex flex-col mt-8 print:mt-0 gap-4 break-before-page">
              <h4>Ongoing SIPs</h4>
              <Table className="border border-border rounded-lg">
                <TableHeader className="bg-muted rounded-t-lg">
                  <TableRow>
                    {sipColumns.map((column) => (
                      <TableHead key={column.name}>{column.name}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSips.map((sip, index) => (
                    <TableRow
                      key={`${sip.scheme_name}-${sip.installment_due_date ?? "na"}-${index}`}
                    >
                      {sipColumns.map((column) => (
                        <TableCell key={column.name}>
                          {column.render(sip)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          {transactions.length ? (
            <div className="mt-8 print:mt-0 break-before-page">
              <OrdersReport transactions={transactions} />
            </div>
          ) : null}
        </div>
      </ReportContainer>
      <ReportFooter>
        {family?.name}&apos;s portfolio valuation report
      </ReportFooter>
    </>
  );
}
